import api from '../api/client';
import LoggingService from '@shared/js/LoggingService';
import storage from '@shared/js/storage';

const STATE = {
	INITIAL:     'initial',
	INITIALIZED: 'initialized',
};

const MESSAGE_NAMESPACE = 'urn:x-cast:tv.asport';
const MESSAGE_CHANNEL_MAX_ATTEMPTS = 3;
const MESSAGE_CHANNEL_RETRY_DELAY_MS = 250;
const MESSAGE_CHANNEL_ACK_TIMEOUT_MS = 1500;
const LOAD_MEDIA_TIMEOUT_MS = 20000;

const EVENT = {
	// base
	INIT:                        'init',
	CASTING_START:               'cast-start',
	CASTING_STOP:                'cast-stop',
	PROGRESS:                    'progress',
	// remote events
	ANY_CHANGE:                  'anyChanged',
	IS_CONNECTED_CHANGED:        'isConnectedChanged',
	IS_MEDIA_LOADED_CHANGED:     'isMediaLoadedChanged',
	DURATION_CHANGED:            'durationChanged',
	CURRENT_TIME_CHANGED:        'currentTimeChanged',
	IS_PAUSED_CHANGED:           'isPausedChanged',
	VOLUME_LEVEL_CHANGED:        'volumeLevelChanged',
	CAN_CONTROL_VOLUME_CHANGED:  'canControlVolumeChanged',
	IS_MUTED_CHANGED:            'isMutedChanged',
	CAN_PAUSE_CHANGED:           'canPauseChanged',
	CAN_SEEK_CHANGED:            'canSeekChanged',
	DISPLAY_NAME_CHANGED:        'displayNameChanged',
	STATUS_TEXT_CHANGED:         'statusTextChanged',
	TITLE_CHANGED:               'titleChanged',
	DISPLAY_STATUS_CHANGED:      'displayStatusChanged',
	MEDIA_INFO_CHANGED:          'mediaInfoChanged',
	IMAGE_URL_CHANGED:           'imageUrlChanged',
	PLAYER_STATE_CHANGED:        'playerStateChanged',
	LIVE_SEEKABLE_RANGE_CHANGED: 'liveSeekableRange',
};

// Reference:
// - https://developers.google.com/cast/docs/web_sender/integrate
// - https://developers.google.com/cast/docs/reference/web_sender/cast.framework.RemotePlayer#volumeLevel
// - https://developers.google.com/cast/docs/reference/web_sender/cast.framework.RemotePlayerController#seek

// const RECEIVER_APP = chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID;
const RECEIVER_APP = '4EA1EA86'; // prod
// const RECEIVER_APP = '18566B80'; // dev

class Chromecast {
	_state = STATE.INITIAL;
	_player = null;
	_playerController = null;
	_eventListeners = Object.values(EVENT).reduce((acc, event) => {
		// @ts-ignore
		acc[event] = [];
		return acc;
	}, {});

	_sessionMessageSubscriptionMap = new Map();
	_pendingMessageChannelMap = new Map();
	_castRequestPromise = null;
	_eventId = null;
	_eventPassword = null;

	constructor() {
		const isBlueTv = window.$Helpers && window.$Helpers.isBlueTv();
		if (isBlueTv) return;

		window.__onGCastApiAvailable = (isAvailable) => {
			if (isAvailable) {
				this.init();
			} else {
				console.log('Chromecast is not available on this client.');
			}
		};
		const script = document.createElement('script');
		script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
		document.body.append(script);
	}

	addEventListener(event, handler) {
		this._eventListeners[event].push(handler);

		if (event === EVENT.INIT && this._state !== STATE.INITIAL) {
			handler();
		}
		if (event === EVENT.CASTING_START && this.currentSession?.getMediaSession?.()) {
			handler();
		}
	}

	setEventId(value) {
		this._eventId = value;
	}

	setEventPassword(value) {
		this._eventPassword = value;
	}

	removeEventListener(event, handler) {
		this._eventListeners[event] = this._eventListeners[event].filter((currentHandler) => currentHandler !== handler);
	}

	emit(event, payload) {
		for (const handler of this._eventListeners[event]) {
			handler(payload);
		}
	}

	get isAvailable() {
		return this._state === STATE.INITIALIZED;
	}

	get isCastRequestPending() {
		return this._castRequestPromise !== null;
	}

	// eslint-disable-next-line class-methods-use-this
	get instance() {
		return window.cast?.framework?.CastContext?.getInstance() ?? null;
	}

	get currentSession() {
		return this.instance?.getCurrentSession() ?? null;
	}

	async createSession() {
		const instance = this.instance;
		if (!instance) {
			throw new Error('Chromecast is not initialized.');
		}

		await instance.requestSession();
		if (!this.currentSession) {
			throw new Error('Casting session was not created.');
		}
		return this.currentSession;
	}

	async connect() {
		const session = this.currentSession ?? (await this.createSession());
		this.addSessionMessageListener(session);
		return session;
	}

	// Register the custom channel as soon as a session exists so it is ready before LOAD starts.
	addSessionMessageListener(session) {
		if (!this._sessionMessageSubscriptionMap.has(session)) {
			session.addMessageListener(MESSAGE_NAMESPACE, (namespace, message) => {
				this.handleSessionMessage(session, namespace, message);
			});
			this._sessionMessageSubscriptionMap.set(session, true);
		}
	}

	// Resolve the receiver readiness handshake before requesting its signed stream URL.
	handleSessionMessage(session, namespace, message) {
		const stage = 'parse-message';

		try {
			const messageData = JSON.parse(message);
			console.log('[chromecast] @message urn:x-cast:tv.asport', { namespace, message, eventId: this._eventId });
			if (messageData.type === 'SENDER_READY_ACK') {
				this._pendingMessageChannelMap.get(messageData.data?.requestId)?.(messageData.data);
			}
		} catch (error) {
			this.reportError(error, stage, session);
			this.emit(EVENT.CASTING_STOP);
		}
	}

	// Keep Cast diagnostics useful without sending stream URLs or event passwords to Sentry.
	reportError(error, stage, session = null) {
		const errorDescription = typeof error?.description === 'string' ? error.description : null;
		const errorMessage = error instanceof Error
			? error.message
			: (typeof error === 'string' ? error : (errorDescription || error?.code || 'Unknown Chromecast error'));
		const reportedError = new Error(`Chromecast ${stage} failed: ${errorMessage}`);

		if (error instanceof Error && error.stack) {
			reportedError.stack = error.stack;
		}

		LoggingService.logError(reportedError, {
			scope:   'chromecast',
			context: {
				stage,
				eventId:              this._eventId,
				sessionState:         session?.getSessionState?.() ?? null,
				castState:            this.instance?.getCastState?.() ?? null,
				isConnected:          this._player?.isConnected ?? false,
				isMediaLoaded:        this._player?.isMediaLoaded ?? false,
				isCastRequestPending: this.isCastRequestPending,
				errorCode:            (typeof error === 'string' ? error : error?.code) ?? null,
				errorDescription,
			},
		});
	}

	// Sending a no-op message establishes the custom channel before LOAD triggers the receiver handshake.
	async prepareMessageChannel(session, requestId) {
		let lastError = null;

		for (let attempt = 1; attempt <= MESSAGE_CHANNEL_MAX_ATTEMPTS; attempt++) {
			let acknowledgeReady;
			const acknowledgmentPromise = new Promise((resolve) => {
				acknowledgeReady = resolve;
			});
			this._pendingMessageChannelMap.set(requestId, acknowledgeReady);

			try {
				await session.sendMessage(MESSAGE_NAMESPACE, {
					type: 'SENDER_READY',
					data: { requestId },
				});
				const acknowledgment = await this.withTimeout(
					acknowledgmentPromise,
					MESSAGE_CHANNEL_ACK_TIMEOUT_MS,
					'Chromecast message channel acknowledgment timed out.'
				);
				if (typeof acknowledgment?.userAgent !== 'string' || acknowledgment.userAgent.length === 0) {
					throw new Error('Chromecast receiver did not provide its user agent.');
				}
				return acknowledgment.userAgent;
			} catch (error) {
				lastError = error;
				if (attempt < MESSAGE_CHANNEL_MAX_ATTEMPTS) {
					await new Promise((resolve) => setTimeout(resolve, MESSAGE_CHANNEL_RETRY_DELAY_MS));
				}
			} finally {
				this._pendingMessageChannelMap.delete(requestId);
			}
		}

		throw lastError;
	}

	get player() {
		if (!this._player) throw new Error('Player is not initialized.');
		return this._player;
	}

	get playerController() {
		if (!this._playerController) throw new Error('Player is not initialized.');
		return this._playerController;
	}

	get state() {
		if (this._state === STATE.INITIAL) return STATE.INITIAL;
		return this.player.playerState;
	}

	get isConnected() {
		return this.player.isConnected;
	}

	get isPaused() {
		return this.player.isPaused;
	}

	get isMediaLoaded() {
		return this.player.isMediaLoaded;
	}

	get position() {
		return this.player.currentTime;
	}

	get duration() {
		return this.player.duration;
	}

	play() {
		if (!this.player.isPaused) return;
		this.playerController.playOrPause();
	}

	pause() {
		if (this.player.isPaused) return;
		this.playerController.playOrPause();
	}

	playOrPause() {
		this.playerController.playOrPause();
	}

	seek(position) {
		this.player.currentTime = position;
		this.playerController.seek();
	}

	setVolume(volume) {
		this.player.volumeLevel = volume / 100;
		this.playerController.setVolumeLevel();
	}

	get isMuted() {
		return this.player.isMuted;
	}

	mute() {
		if (this.player.isMuted) return;
		this.playerController.muteOrUnmute();
	}

	unmute() {
		if (!this.player.isMuted) return;
		this.playerController.muteOrUnmute();
	}

	async init() {
		const castFramework = window.cast?.framework;
		const chromeCast = window.chrome?.cast;

		if (!castFramework || !chromeCast?.AutoJoinPolicy) return;

		const instance = this.instance;
		if (!instance) return;

		instance.setOptions({
			receiverApplicationId: RECEIVER_APP,
			autoJoinPolicy:        chromeCast.AutoJoinPolicy.ORIGIN_SCOPED,
		});

		instance.addEventListener(castFramework.CastContextEventType.SESSION_STATE_CHANGED, (event) => {
			console.log('SESSION_STATE_CHANGED', event);
			if ([castFramework.SessionState.SESSION_STARTED, castFramework.SessionState.SESSION_RESUMED].includes(event.sessionState)) {
				try {
					this.addSessionMessageListener(event.session ?? this.currentSession);
				} catch (error) {
					this.reportError(error, 'register-message-listener', event.session);
				}
				if (event.sessionState === castFramework.SessionState.SESSION_RESUMED &&
					(event.session ?? this.currentSession)?.getMediaSession?.()) {
					this.emit(EVENT.CASTING_START);
				}
			}
			if (event.sessionState === castFramework.SessionState.SESSION_ENDED) {
				this.emit(EVENT.CASTING_STOP);
			}

			// requestSession() reports this failure through its rejected promise as well.
			// Let the active cast request report it once with the exact failing stage.
			if (event.sessionState === castFramework.SessionState.SESSION_START_FAILED &&
				event.errorCode !== chromeCast.ErrorCode.CANCEL &&
				!this.isCastRequestPending) {
				this.reportError(new Error(`Chromecast session start failed: ${event.errorCode}`), 'start-session', event.session);
			}

			if (event.sessionState === castFramework.SessionState.SESSION_START_FAILED) {
				this.emit(EVENT.CASTING_STOP);
			}
		});

		this._player = new castFramework.RemotePlayer();
		this._playerController = new castFramework.RemotePlayerController(this._player);

		// any change
		this._playerController.addEventListener(castFramework.RemotePlayerEventType.ANY_CHANGE, () => {
			this.emit(EVENT.ANY_CHANGE);
		});

		// media info change (currentTime, metadata)
		this._playerController.addEventListener(castFramework.RemotePlayerEventType.MEDIA_INFO_CHANGED, () => {
			if (this.currentSession) {
				const mediaSession = this.currentSession.getMediaSession();
				if (mediaSession) {
					const position = mediaSession.getEstimatedTime();
					this.emit(EVENT.PROGRESS, position);
				}
			}

			this.emit(EVENT.MEDIA_INFO_CHANGED);
		});

		this._playerController.addEventListener(castFramework.RemotePlayerEventType.CURRENT_TIME_CHANGED, () => {
			if (Number.isFinite(this.player.currentTime)) {
				this.emit(EVENT.PROGRESS, this.player.currentTime);
			}
			this.emit(EVENT.CURRENT_TIME_CHANGED);
		});

		this._playerController.addEventListener(castFramework.RemotePlayerEventType.IS_CONNECTED_CHANGED, () => {
			console.log('IS_CONNECTED_CHANGED', this.isConnected);
			if (!this.player.isConnected) {
				this.emit(EVENT.CASTING_STOP);
			}
			this.emit(EVENT.IS_CONNECTED_CHANGED);
		});

		this._playerController.addEventListener(castFramework.RemotePlayerEventType.IS_MEDIA_LOADED_CHANGED, () => {
			// A resumed session may publish its media state after SESSION_RESUMED.
			// New requests switch only after loadMedia() resolves below.
			if (this.isMediaLoaded && !this.isCastRequestPending) {
				this.emit(EVENT.CASTING_START);
			}
			this.emit(EVENT.IS_MEDIA_LOADED_CHANGED);
		});

		this._state = STATE.INITIALIZED;
		this.emit(EVENT.INIT);
	}

	/**
	 * Cast media to the Chromecast device
	 * @param {Object} options - Cast options
	 * @param {number|null} options.audioTrackId - Audio track index to select
	 * @param {string|null} options.audioTrackLanguage - HLS language of the selected audio track
	 * @param {string|null} options.audioTrackName - HLS name of the selected audio track
	 * @param {number} options.position - Fallback playback position (currentTime)
	 * @param {Function|null} options.getPosition - Reads the playback position immediately before LOAD
	 * @param {string} options.contentType - MIME type
	 * @param {boolean} options.isLive - Whether this is a live stream
	 * @param {boolean} options.isPreview - Whether the event is being watched in preview mode
	 * @param {Object|null} options.liveSeekableRange - For live streams with DVR, pass the video element's seekable range:
	 *   { start: video.seekable.start(0), end: video.seekable.end(0) }
	 *   This enables the Chromecast to start playback at the same position relative to live edge,
	 *   and provides the full DVR window context.
	 * @param {Function|null} options.getLiveSeekableRange - Reads the live seekable range immediately before LOAD
	 */
	async castMedia(options = {}) {
		if (this._castRequestPromise) return this._castRequestPromise;

		this._castRequestPromise = this.startCasting(options);
		try {
			return await this._castRequestPromise;
		} finally {
			this._castRequestPromise = null;
		}
	}

	// Run one complete Cast request at a time so repeated clicks cannot overlap requestSession() calls.
	async startCasting({
		audioTrackId = null,
		audioTrackLanguage = null,
		audioTrackName = null,
		position = 0,
		getPosition = null,
		contentType = 'application/x-mpegurl',
		isLive = false,
		isPreview = false,
		liveSeekableRange = null,
		getLiveSeekableRange = null,
	}) {
		let stage = 'connect-session';
		let session = null;
		const requestId = window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
		const eventId = Number(this._eventId);

		try {
			session = await this.connect();

			stage = 'prepare-message-channel';
			const receiverUserAgent = await this.prepareMessageChannel(session, requestId);

			stage = 'request-playout';
			const stream = await api.getEventStream(eventId, this._eventPassword, receiverUserAgent);
			const streamUrl = stream?.hlsUrl;
			if (!streamUrl) throw new Error('Playout response did not contain a stream URL.');

			stage = 'create-load-request';
			const latestPosition = typeof getPosition === 'function' ? getPosition() : position;
			const playbackPosition = Number.isFinite(latestPosition) ? latestPosition : position;
			const latestLiveSeekableRange = typeof getLiveSeekableRange === 'function'
				? getLiveSeekableRange()
				: liveSeekableRange;
			const mediaInfo = new chrome.cast.media.MediaInfo(streamUrl, contentType);
			mediaInfo.streamType = isLive ? chrome.cast.media.StreamType.LIVE : chrome.cast.media.StreamType.BUFFERED;

			const request = new chrome.cast.media.LoadRequest(mediaInfo);
			request.autoplay = true;
			request.currentTime = playbackPosition;

			const audioTrackData = {
				audioTrackId,
				audioTrackLanguage,
				audioTrackName,
			};
			const analyticsData = {
				analytics: {
					version:  1,
					eventId,
					viewerId: storage.viewerId,
					source:   window.location.href,
					referrer: document.referrer !== window.location.href ? document.referrer : '',
					preview:  isPreview,
				},
			};

			// For live streams, include additional positioning data
			// so the receiver can seek to the correct position within the DVR window
			if (isLive && latestLiveSeekableRange) {
				const offsetFromLiveEdge = latestLiveSeekableRange.end - playbackPosition;

				request.customData = {
					...audioTrackData,
					...analyticsData,
					requestId,
					eventId,
					isLive:            true,
					// The playback position at the time of casting
					livePosition:      playbackPosition,
					// How many seconds behind the live edge the user was watching
					offsetFromLiveEdge,
					// The full seekable range from the browser player
					liveSeekableRange: {
						start: latestLiveSeekableRange.start,
						end:   latestLiveSeekableRange.end,
					},
				};
			} else {
				request.customData = {
					...audioTrackData,
					...analyticsData,
					requestId,
					eventId,
				};
			}

			stage = 'load-media';
			await this.withTimeout(session.loadMedia(request), LOAD_MEDIA_TIMEOUT_MS, 'Chromecast LOAD timed out.');
			this.emit(EVENT.CASTING_START);
			return true;
		} catch (error) {
			if (error !== window.chrome?.cast?.ErrorCode?.CANCEL && error?.code !== window.chrome?.cast?.ErrorCode?.CANCEL) {
				this.reportError(error, stage, session);
			}
			this.emit(EVENT.CASTING_STOP);
			return false;
		}
	}

	// Prevent a missing receiver response from blocking all later Cast attempts.
	async withTimeout(promise, timeoutMs, errorMessage) {
		let timeoutId;
		const timeoutPromise = new Promise((resolve, reject) => {
			timeoutId = setTimeout(() => reject(new Error(errorMessage)), timeoutMs);
		});

		try {
			return await Promise.race([promise, timeoutPromise]);
		} finally {
			clearTimeout(timeoutId);
		}
	}

	stopCasting() {
		this.playerController.stop();
		if (!this.currentSession) return;
		this.currentSession.endSession(true);
	}
}

export { Chromecast, EVENT };
export default Chromecast;
